import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CalendarRange, Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectSlotWindows, selectSlotWindowsStatus } from "../../features/bookable-slots/bookable-slots-slice";
import { BookableSlotWindowRequest } from "../../features/bookable-slots/contracts/BookableSlotWindowRequest";
import { WeekDay } from "../../features/bookable-slots/enums/WeekDay";
import {
  fetchSlotWindows,
  saveSlotWindows,
} from "../../features/bookable-slots/slice-extensions/bookable-slots-slice-extension";
import { useVocabulary } from "../../features/organization/hooks/useVocabulary";
import { BookingMode } from "../../features/sessions/enums/BookingMode";
import { selectTopicChoices } from "../../features/training/training-slice";
import { fetchTopicChoices } from "../../features/training/slice-extensions/training-topics-slice-extension";
import BookableSlotWindowRow from "./BookableSlotWindowRow";

const NEW_WINDOW: BookableSlotWindowRequest = {
  dayOfWeek: WeekDay.Tuesday,
  startLocal: "14:00",
  endLocal: "18:00",
  unitMinutes: 45,
  bookingMode: BookingMode.OpenWithConfirmation,
  defaultTopicId: null,
};

/**
 * "Bookable by {{members}}" on the Time slots page: the weekly windows members may request
 * individual slots in, from the portal. Times are the instructor's own wall clock; a save
 * replaces the whole set.
 */
const BookableSlotWindowsCard = () => {
  const { t } = useTranslation();
  const vocabulary = useVocabulary();
  const dispatch = useAppDispatch();
  const saved = useAppSelector(selectSlotWindows);
  const status = useAppSelector(selectSlotWindowsStatus);
  const topicChoices = useAppSelector(selectTopicChoices);
  const [draft, setDraft] = useState<BookableSlotWindowRequest[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchSlotWindows());
    dispatch(fetchTopicChoices());
  }, [dispatch]);

  useEffect(() => {
    setDraft(
      saved.map(({ dayOfWeek, startLocal, endLocal, unitMinutes, bookingMode, defaultTopicId }) => ({
        dayOfWeek,
        startLocal,
        endLocal,
        unitMinutes,
        bookingMode,
        defaultTopicId,
      })),
    );
  }, [saved]);

  const practicalTopics = useMemo(() => topicChoices.filter((c) => c.kind === "Practical"), [topicChoices]);

  const update = (index: number, window: BookableSlotWindowRequest) =>
    setDraft((rows) => rows.map((row, i) => (i === index ? window : row)));

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const result = await dispatch(saveSlotWindows({ windows: draft }));
    setBusy(false);
    if (saveSlotWindows.fulfilled.match(result)) {
      toast.success(t("timeSlots.bookable.saved"));
    } else {
      setError(result.payload?.message || t("timeSlots.bookable.failed"));
    }
  };

  if (status === "idle" || status === "loading") return null;

  return (
    <div className="with-border rounded-2xl bg-white p-4 lg:p-6 dark:bg-primary-10">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-grey-20 dark:text-white">
        <CalendarRange size={20} />
        {t("timeSlots.bookable.title", vocabulary)}
      </h2>
      <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">{t("timeSlots.bookable.hint", vocabulary)}</p>

      {draft.length === 0 ? (
        <p className="mt-4 text-sm text-grey-40 dark:text-grey-80">{t("timeSlots.bookable.empty")}</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {draft.map((window, index) => (
            <BookableSlotWindowRow
              key={index}
              index={index}
              window={window}
              topics={practicalTopics}
              onChange={(changed) => update(index, changed)}
              onRemove={() => setDraft((rows) => rows.filter((_, i) => i !== index))}
            />
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setDraft((rows) => [...rows, NEW_WINDOW])}
          disabled={busy || draft.length >= 28}
        >
          <Plus size={18} />
          {t("timeSlots.bookable.add")}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy}>
          {busy ? t("common.saving") : t("timeSlots.bookable.save")}
        </button>
      </div>
    </div>
  );
};

export default BookableSlotWindowsCard;
